import {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {invokeVoiceTool, startWebRTCRealtimeVoiceSession} from 'llm/data-capture/DataCaptureDAO';
import {
  isFunctionCallDoneEvent,
  createToolResponseEvent,
  createTriggerInferenceEvent,
} from 'llm/realtime-voice/OpenAIRealtimeEvents';

import WebRTCSession from './WebRTCSession';

export enum RealtimeVoiceSessionState {
  NOT_CONNECTED = 'NOT_CONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
}

export interface RealtimeVoiceSessionControls {
  sessionState: RealtimeVoiceSessionState;
  startVoiceSession: () => Promise<void>;
  stopVoiceSession: () => Promise<void>;
}

/**
 * Hook to manage a realtime voice session.
 *
 * Currently this is implemented as a WebRTC connection to OpenAI's Realtime API.
 */
export default function useRealtimeVoiceSession(chatSessionId: string | null): RealtimeVoiceSessionControls {
  const [sessionState, setSessionState] = useState<RealtimeVoiceSessionState>(
    RealtimeVoiceSessionState.NOT_CONNECTED
  );
  const webRTCSessionRef = useRef<WebRTCSession | null>(null);
  const sessionStartTimeMsRef = useRef<number | null>(null);
  // Tracks whether the component is still mounted. Used to detect unmount during async operations
  // where webRTCSessionRef.current may not yet be set (e.g., during WebRTCSession.create()).
  const isMountedRef = useRef(true);

  // On unmount, disconnect the session, release the microphone, and mark as unmounted.
  useEffect(
    () => () => {
      isMountedRef.current = false;
      webRTCSessionRef.current?.close();
      webRTCSessionRef.current = null;
    },
    []
  );

  const handleDataEvent = useCallback(
    async (data: unknown) => {
      assert(chatSessionId != null, 'Chat session ID should be set during voice session');
      // Capture the current session at the start. After awaits, we verify we're still on the same
      // session to prevent sending responses to a different session if the user restarts voice.
      const webRTCSession = webRTCSessionRef.current;
      if (webRTCSession == null) {
        return;
      }
      if (isFunctionCallDoneEvent(data)) {
        // Handle OpenAI tool calls. For now, we block on tool call completion and send the result
        // to the model, and we expect errors for the LLM to be sent as regular text results.
        // In the future, we may make this more nuanced to allow truly async tool calls that don't
        // stop the conversation while they run.
        const {name, call_id: callId, arguments: args} = data.item;
        const {output} = await invokeVoiceTool(chatSessionId, {name, callId, args});
        // Verify we're still on the same session - abort if unmounted or session was restarted.
        if (webRTCSessionRef.current !== webRTCSession) {
          return;
        }
        webRTCSession.sendDataEvent(createToolResponseEvent(callId, output));
        webRTCSession.sendDataEvent(createTriggerInferenceEvent());
      }
    },
    [chatSessionId]
  );

  const stopVoiceSession = useCallback(async () => {
    await webRTCSessionRef.current?.close();
    webRTCSessionRef.current = null;
    setSessionState(RealtimeVoiceSessionState.NOT_CONNECTED);

    // Track analytics for session end. Note that we don't currently track this when closing the
    // tab or navigating away from the page.
    if (chatSessionId != null && sessionStartTimeMsRef.current != null) {
      const durationMs = Date.now() - sessionStartTimeMsRef.current;
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ENDED_COMPOSE_REALTIME_VOICE_SESSION, {
        chatSessionId,
        durationMs,
      });
      sessionStartTimeMsRef.current = null;
    }
  }, [chatSessionId]);

  const startVoiceSession = useCallback(async () => {
    assert(chatSessionId != null, 'Unexpectedly started voice session without a chat session ID.');
    setSessionState(RealtimeVoiceSessionState.CONNECTING);

    try {
      // Create into a local variable first, then check if we should abort before assigning.
      // This handles unmount during WebRTCSession.create() (e.g., during mic permission prompt).
      const webRTCSession = await WebRTCSession.create(handleDataEvent);
      if (!isMountedRef.current) {
        webRTCSession.close();
        return;
      }
      webRTCSessionRef.current = webRTCSession;

      const initialSDP = await webRTCSession.createSDPOffer();
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      const {sdpAnswer} = await startWebRTCRealtimeVoiceSession(chatSessionId, initialSDP);
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      await webRTCSession.startSessionWithSDPAnswer(sdpAnswer);
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      setSessionState(RealtimeVoiceSessionState.CONNECTED);
      sessionStartTimeMsRef.current = Date.now();
    } catch (error) {
      stopVoiceSession();
      throw error;
    }
  }, [chatSessionId, stopVoiceSession, handleDataEvent]);

  return {sessionState, startVoiceSession, stopVoiceSession};
}
